import React, { useState, useEffect } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
} from '@chakra-ui/modal';
import InputField from 'components/fields/InputField';
import TextField from 'components/fields/TextField';
import { AiOutlineLoading } from 'react-icons/ai';
import { useTranslation } from 'react-i18next';

export interface Workflow {
  id: string;
  name: string;
  description?: string;
  content: string[];
  folder_id?: string;
  workspace_id?: string;
  created_at?: string;
  updated_at?: string;
  created_by?: string;
  updated_by?: string;
}
interface Step {
  id: string;
  value: string;
}

const AddIcon = () => (
  <svg
    width="1em"
    height="1em"
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 4C11.4477 4 11 4.44772 11 5V11H5C4.44772 11 4 11.4477 4 12C4 12.5523 4.44772 13 5 13H11V19C11 19.5523 11.4477 20 12 20C12.5523 20 13 19.5523 13 19V13H19C19.5523 13 20 12.5523 20 12C20 11.4477 19.5523 11 19 11H13V5C13 4.44772 12.5523 4 12 4Z"
      fill="currentColor"
    />
  </svg>
);

const DeleteIcon = () => (
  <svg
    width="1em"
    height="1em"
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M17 6H22V8H20V21C20 21.5523 19.5523 22 19 22H5C4.44772 22 4 21.5523 4 21V8H2V6H7V4C7 3.44772 7.44772 3 8 3H16C16.5523 3 17 3.44772 17 4V6ZM18 8H6V20H18V8ZM9 11V17H11V11H9ZM13 11V17H15V11H13Z"
      fill="currentColor"
    />
  </svg>
);

const DragHandleIcon = () => (
  <svg
    width="1.2em"
    height="1.2em"
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
    className="cursor-grab"
  >
    <path d="M10 4h4v4h-4zM10 10h4v4h-4zM10 16h4v4h-4zM4 10h4v4H4zM16 10h4v4h-4z" />
  </svg>
);

interface SortableStepProps {
  id: string;
  index: number;
  step: string;
  handleStepChange: (index: number, value: string) => void;
  handleRemoveStep: (index: number) => void;
  loading: boolean;
  handleEnterPress: (index: number) => void;
}

const SortableStep: React.FC<SortableStepProps> = ({
  id,
  index,
  step,
  handleStepChange,
  handleRemoveStep,
  loading,
  handleEnterPress,
}) => {
  const { t } = useTranslation();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleEnterPress(index);
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className="flex items-center gap-2"
    >
      <button {...listeners} className="mt-2 text-gray-500">
        <DragHandleIcon />
      </button>
      <InputField
        id={`step-${index}`}
        placeholder={t("workflows.dialog.stepPlaceholder", { number: index + 1 })}
        value={step}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          handleStepChange(index, e.target.value)
        }
        onKeyPress={handleKeyDown}
        extra="w-full"
        disabled={loading}
      />
      <button
        onClick={() => handleRemoveStep(index)}
        className="mt-2 text-red-500 hover:text-red-700 disabled:opacity-50"
        disabled={loading}
      >
        <DeleteIcon />
      </button>
    </div>
  );
};

interface WorkflowDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: Partial<Workflow>) => void;
  initialData?: Workflow;
  loading?: boolean;
  error?: string | null;
  folderId?: string;
}

const WorkflowDialog: React.FC<WorkflowDialogProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  loading = false,
  error = null,
  folderId,
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [steps, setSteps] = useState<Step[]>([{ id: '1', value: '' }]);
  const [focusOnNewStep, setFocusOnNewStep] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const isEditMode = !!initialData;

  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        setName(initialData.name || '');
        setDescription(initialData.description || '');
        setSteps(
          initialData.content && initialData.content.length > 0
            ? initialData.content.map((s, i) => ({ id: `${i + 1}`, value: s }))
            : [{ id: '1', value: '' }],
        );
      } else {
        setName('');
        setDescription('');
        setSteps([{ id: '1', value: '' }]);
      }
    }
  }, [initialData, isOpen]);

  useEffect(() => {
    if (focusOnNewStep) {
      const lastStepIndex = steps.length - 1;
      const lastStepInput = document.getElementById(`step-${lastStepIndex}`);
      if (lastStepInput) {
        lastStepInput.focus();
      }
      setFocusOnNewStep(false);
    }
  }, [steps, focusOnNewStep]);

  const handleAddStep = () => {
    setSteps([...steps, { id: `${Date.now()}`, value: '' }]);
    setFocusOnNewStep(true);
  };

  const handleRemoveStep = (index: number) => {
    const newSteps = steps.filter((_, i) => i !== index);
    if (newSteps.length === 0) {
      setSteps([{ id: '1', value: '' }]);
    } else {
      setSteps(newSteps);
    }
  };

  const handleStepChange = (index: number, value: string) => {
    const newSteps = [...steps];
    newSteps[index].value = value;
    setSteps(newSteps);
  };

  const handleEnterPress = (index: number) => {
    if (index === steps.length - 1) {
      handleAddStep();
    } else {
      const nextStepInput = document.getElementById(`step-${index + 1}`);
      if (nextStepInput) {
        nextStepInput.focus();
      }
    }
  };

  const handleSave = () => {
    const payload: Partial<Workflow> = {
      name,
      description,
      content: steps.map((step) => step.value).filter((step) => step.trim() !== ''),
    };
    if (isEditMode) {
      payload.id = initialData.id;
    } else {
      payload.folder_id = folderId;
    }
    onSubmit(payload);
  };
  
  function handleDragEnd(event: DragEndEvent) {
    const {active, over} = event;
    
    if (over && active.id !== over.id) {
      setSteps((items) => {
        const oldIndex = items.findIndex(item => item.id === active.id);
        const newIndex = items.findIndex(item => item.id === over.id);
        
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  }

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      placement={document.documentElement.dir === 'rtl' ? 'left' : 'right'}
    >
      <div
        className={`
              bg-black/60 dark:bg-black/80 fixed inset-0 backdrop-blur-sm
              transition-opacity duration-300 ease-out
            `}
      />
      <DrawerContent className="my-4 ml-0 mr-4 w-full max-w-lg rounded-2xl bg-white shadow-[-20px_17px_40px_4px_rgba(112,_144,_176,_0.18)] dark:bg-navy-800 dark:shadow-[-22px_32px_51px_4px_#0B1437]">
        <DrawerHeader px="28px" pt="24px" pb="0px">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xl font-bold text-gray-900 dark:text-white">
                {isEditMode ? t("workflows.dialog.editTitle") : t("workflows.dialog.createTitle")}
              </p>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                {isEditMode
                  ? t("workflows.dialog.editDescription")
                  : t("workflows.dialog.createDescription")}
              </p>
            </div>
          </div>
        </DrawerHeader>
        <DrawerCloseButton className="absolute right-[26px] top-[16px] h-4 w-4 text-gray-900 dark:text-white" />
        <DrawerBody px="28px" pt="16px" pb="24px" height="100%" overflow="hidden">
          <div className="space-y-4" style={{ height: 'calc(100% - 53px)', overflow: 'auto'}}>
            {error && (
              <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-600">
                {error}
              </div>
            )}
            <InputField
              id="workflowName"
              label={t("workflows.dialog.nameLabel")}
              placeholder={t("workflows.dialog.namePlaceholder")}
              value={name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setName(e.target.value)
              }
              disabled={loading}
            />
            <TextField
              id="workflowDescription"
              label={t("workflows.dialog.descriptionLabel")}
              placeholder={t("workflows.dialog.descriptionPlaceholder")}
              value={description}
              rows={4}
              cols={4}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                setDescription(e.target.value)
              }
              disabled={loading}
            />
            <div>
              <label className="ml-3 text-sm font-bold text-navy-700 dark:text-white">
                {t("workflows.dialog.stepsLabel")}
              </label>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={steps.map(s => s.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="mt-1 space-y-2">
                    {steps.map((step, index) => (
                      <SortableStep
                        key={step.id}
                        id={step.id}
                        index={index}
                        step={step.value}
                        handleStepChange={handleStepChange}
                        handleRemoveStep={handleRemoveStep}
                        loading={loading}
                        handleEnterPress={handleEnterPress}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
              <button
                  onClick={handleAddStep}
                  className="mt-4 flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-800 disabled:opacity-50"
                  disabled={loading}
                >
                  <AddIcon />
                  <span>{t("workflows.dialog.addStep")}</span>
                </button>
            </div>
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <button
              onClick={onClose}
              disabled={loading}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              {t("workflows.dialog.cancel")}
            </button>
            <button
              onClick={handleSave}
              disabled={loading}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <AiOutlineLoading className="animate-spin" />
                  {isEditMode ? t("workflows.dialog.saving") : t("workflows.dialog.creating")}
                </span>
              ) : isEditMode ? (
                t("workflows.dialog.saveChanges")
              ) : (
                t("workflows.dialog.create")
              )}
            </button>
          </div>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export default WorkflowDialog; 